// Localização: frontend/src/utils/gerarProntuarioPDF.js (Versão 1.0.0)
// Função utilitária para gerar PDF completo do prontuário do paciente

import jsPDF from 'jspdf';
import {
    getProntuarioSummary,
    getHistoricoSinaisVitais,
    getHistoricoCuidados,
    getHistoricoMedico,
    getAlergias,
    getVacinas,
    getExames,
    get
} from '../api/api';
import { getPacienteFotoUrl } from './imageUtils';

/**
 * Gera um PDF completo do prontuário do paciente com todas as informações cruciais para internação
 * @param {number} pacienteId - ID do paciente
 */
export const gerarProntuarioPDF = async (pacienteId) => {
    try {
        // Buscar todos os dados necessários
        const [
            summaryResponse,
            sinaisVitaisResponse,
            cuidadosResponse,
            historicoMedicoResponse,
            alergiasResponse,
            vacinasResponse,
            examesResponse,
            planosResponse
        ] = await Promise.all([
            getProntuarioSummary(pacienteId),
            getHistoricoSinaisVitais(pacienteId).catch(() => ({ data: { data: [] } })),
            getHistoricoCuidados(pacienteId, { limit: 20 }).catch(() => ({ data: { data: [] } })),
            getHistoricoMedico(pacienteId).catch(() => ({ data: { data: [] } })),
            getAlergias(pacienteId).catch(() => ({ data: { data: [] } })),
            getVacinas(pacienteId).catch(() => ({ data: { data: [] } })),
            getExames(pacienteId).catch(() => ({ data: { data: [] } })),
            get(`/planos-medicos/paciente/${pacienteId}`).catch(() => ({ data: { data: [] } }))
        ]);

        const paciente = summaryResponse.data.data.paciente;
        const ultimosSinaisVitais = summaryResponse.data.data.ultimosSinaisVitais;
        const alertasMedicacao = summaryResponse.data.data.alertasMedicacao || [];
        const sinaisVitais = sinaisVitaisResponse.data?.data || [];
        const cuidados = cuidadosResponse.data?.data || [];
        const historicoMedico = historicoMedicoResponse.data?.data || [];
        const alergias = alergiasResponse.data?.data || [];
        const vacinas = vacinasResponse.data?.data || [];
        const exames = examesResponse.data?.data || [];
        const planos = planosResponse.data?.data || [];
        
        // Combinar todos os registros em um único array para ordenação temporal
        const todosRegistros = [];
        
        // Adicionar doenças
        historicoMedico.filter(item => item.tipo === 'doenca').forEach(item => {
            todosRegistros.push({
                ...item,
                tipoRegistro: 'doenca',
                dataReferencia: item.data_ocorrencia || item.data_registro
            });
        });
        
        // Adicionar cirurgias
        historicoMedico.filter(item => item.tipo === 'cirurgia').forEach(item => {
            todosRegistros.push({
                ...item,
                tipoRegistro: 'cirurgia',
                dataReferencia: item.data_ocorrencia || item.data_registro
            });
        });
        
        // Adicionar hospitalizações
        historicoMedico.filter(item => item.tipo === 'hospitalizacao').forEach(item => {
            todosRegistros.push({
                ...item,
                tipoRegistro: 'hospitalizacao',
                dataReferencia: item.data_internacao || item.data_ocorrencia || item.data_registro
            });
        });
        
        // Adicionar alergias
        alergias.forEach(item => {
            todosRegistros.push({
                ...item,
                tipoRegistro: 'alergia',
                dataReferencia: item.data_descoberta || item.data_registro
            });
        });
        
        // Adicionar vacinas
        vacinas.forEach(item => {
            todosRegistros.push({
                ...item,
                tipoRegistro: 'vacina',
                dataReferencia: item.data_aplicacao || item.data_registro
            });
        });
        
        // Adicionar exames
        exames.forEach(item => {
            todosRegistros.push({
                ...item,
                tipoRegistro: 'exame',
                dataReferencia: item.data_realizacao || item.data_resultado || item.data_registro
            });
        });
        
        // Ordenar por data (mais antigo primeiro)
        todosRegistros.sort((a, b) => {
            const dataA = a.dataReferencia ? new Date(a.dataReferencia).getTime() : 0;
            const dataB = b.dataReferencia ? new Date(b.dataReferencia).getTime() : 0;
            return dataA - dataB;
        });

        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const margin = 20;
        const dataAtual = new Date().toLocaleDateString('pt-BR');
        const horaAtual = new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

        // Cores padronizadas (mesmo padrão das notas clínicas)
        const corEmerald = [16, 185, 129];
        const corGray = [107, 114, 128];

        // Função auxiliar para adicionar texto com quebra de linha automática e justificação
        const adicionarTextoComQuebra = (texto, x, y, maxWidth, fontSize = null, lineHeight = 6, justificar = true) => {
            if (!texto || typeof texto !== 'string') return y;
            
            const tamanhoAtual = doc.getFontSize();
            if (fontSize) {
                doc.setFontSize(fontSize);
            }
            
            const lines = doc.splitTextToSize(texto, maxWidth);
            let currentY = y;
            
            lines.forEach((line, index) => {
                if (currentY > pageHeight - 40) {
                    doc.addPage();
                    currentY = margin;
                }
                
                // Justificar linhas completas (exceto a última)
                const isLastLine = index === lines.length - 1;
                const lineWidth = doc.getTextWidth(line);
                const shouldJustify = justificar && !isLastLine && lineWidth < maxWidth * 0.9;
                
                // Todas as linhas começam na mesma posição X para manter alinhamento
                if (shouldJustify) {
                    doc.text(line, x, currentY, {
                        align: 'justify',
                        maxWidth: maxWidth
                    });
                } else {
                    doc.text(line, x, currentY, {
                        maxWidth: maxWidth
                    });
                }
                currentY += lineHeight;
            });
            
            if (fontSize) {
                doc.setFontSize(tamanhoAtual);
            }
            
            return currentY;
        };
        
        // Função auxiliar para adicionar label + conteúdo com quebra de linha e alinhamento correto
        const adicionarLabelEConteudo = (label, conteudo, x, y, maxWidth, fontSize = null, lineHeight = 6, justificar = true) => {
            if (!conteudo || typeof conteudo !== 'string') return y;
            
            const tamanhoAtual = doc.getFontSize();
            if (fontSize) {
                doc.setFontSize(fontSize);
            }
            
            // Calcular largura do label para saber onde começar o conteúdo
            const labelWidth = doc.getTextWidth(label);
            const conteudoX = x + labelWidth;
            const conteudoMaxWidth = maxWidth - labelWidth;
            
            // Quebrar apenas o conteúdo (sem o label)
            const lines = doc.splitTextToSize(conteudo, conteudoMaxWidth);
            let currentY = y;
            
            lines.forEach((line, index) => {
                if (currentY > pageHeight - 40) {
            doc.addPage();
                    currentY = margin;
                }
                
                // Primeira linha: label + primeira parte do conteúdo
                if (index === 0) {
                    const primeiraLinha = label + line;
                    const primeiraLinhaWidth = doc.getTextWidth(primeiraLinha);
                    const isLastLine = lines.length === 1;
                    const shouldJustify = justificar && !isLastLine && primeiraLinhaWidth < maxWidth * 0.9;
                    
                    if (shouldJustify) {
                        doc.text(primeiraLinha, x, currentY, {
                            align: 'justify',
                            maxWidth: maxWidth
                        });
                    } else {
                        doc.text(primeiraLinha, x, currentY, {
                            maxWidth: maxWidth
                        });
                    }
                } else {
                    // Linhas subsequentes: apenas o conteúdo, alinhado com o início do conteúdo da primeira linha
                    const lineWidth = doc.getTextWidth(line);
                    const isLastLine = index === lines.length - 1;
                    const shouldJustify = justificar && !isLastLine && lineWidth < conteudoMaxWidth * 0.9;
                    
                    if (shouldJustify) {
                        doc.text(line, conteudoX, currentY, {
                            align: 'justify',
                            maxWidth: conteudoMaxWidth
                        });
                    } else {
                        doc.text(line, conteudoX, currentY, {
                            maxWidth: conteudoMaxWidth
                        });
                    }
                }
                currentY += lineHeight;
            });
            
            if (fontSize) {
                doc.setFontSize(tamanhoAtual);
            }
            
            return currentY;
        };

        // Cabeçalho verde no topo (padrão das notas clínicas)
        doc.setFillColor(...corEmerald);
        doc.rect(0, 0, pageWidth, 40, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(18);
        doc.setFont(undefined, 'bold');
        doc.text('PRONTUÁRIO MÉDICO', margin, 25);

        let yPos = 50;

        // ============================================
        // FOTO DO PACIENTE E INFORMAÇÕES BÁSICAS
        // ============================================
        
        // Foto do paciente (circular) - reduzida para evitar sobreposição
        const fotoSize = 28; // Reduzido de 36 para 28
        const fotoX = margin;
        const fotoY = yPos; // yPos já está em 50, abaixo da barra verde
        const renderScale = 4; // Renderizar em 4x para alta qualidade
        
        try {
            const fotoUrl = getPacienteFotoUrl(paciente.fotoURL);
            const img = new Image();
            img.crossOrigin = 'anonymous';
            
            await new Promise((resolve) => {
                img.onload = () => {
                    try {
                        // Criar canvas com alta resolução (4x) para máxima qualidade
                        const canvas = document.createElement('canvas');
                        const canvasSize = fotoSize * renderScale;
                        canvas.width = canvasSize;
                        canvas.height = canvasSize;
                        const ctx = canvas.getContext('2d');
                        
                        // Habilitar suavização de alta qualidade
                        ctx.imageSmoothingEnabled = true;
                        ctx.imageSmoothingQuality = 'high';
                        
                        // Desenhar círculo de fundo branco
                        ctx.fillStyle = '#FFFFFF';
                        ctx.beginPath();
                        ctx.arc(canvasSize / 2, canvasSize / 2, canvasSize / 2, 0, 2 * Math.PI);
                        ctx.fill();
                        
                        // Criar clipping path circular
                        ctx.beginPath();
                        ctx.arc(canvasSize / 2, canvasSize / 2, canvasSize / 2, 0, 2 * Math.PI);
                        ctx.clip();
                        
                        // Desenhar foto dentro do círculo com alta resolução
                        // Usar a dimensão original da imagem se disponível para melhor qualidade
                        const imgWidth = img.naturalWidth || img.width;
                        const imgHeight = img.naturalHeight || img.height;
                        
                        // Calcular posição para centralizar e preencher o círculo
                        const scale = Math.max(canvasSize / imgWidth, canvasSize / imgHeight);
                        const scaledWidth = imgWidth * scale;
                        const scaledHeight = imgHeight * scale;
                        const x = (canvasSize - scaledWidth) / 2;
                        const y = (canvasSize - scaledHeight) / 2;
                        
                        ctx.drawImage(img, x, y, scaledWidth, scaledHeight);
                        
                        // Usar PNG com qualidade máxima
                        const circularPhoto = canvas.toDataURL('image/png', 1.0);
                        doc.addImage(circularPhoto, 'PNG', fotoX, fotoY, fotoSize, fotoSize);
                    } catch (err) {
                        console.warn('Erro ao processar foto:', err);
                        // Desenhar placeholder circular se falhar
                        const canvas = document.createElement('canvas');
                        canvas.width = fotoSize;
                        canvas.height = fotoSize;
                        const ctx = canvas.getContext('2d');
                        ctx.fillStyle = '#E5E7EB';
                        ctx.beginPath();
                        ctx.arc(fotoSize / 2, fotoSize / 2, fotoSize / 2, 0, 2 * Math.PI);
                        ctx.fill();
                        const placeholder = canvas.toDataURL('image/png');
                        doc.addImage(placeholder, 'PNG', fotoX, fotoY, fotoSize, fotoSize);
                    }
                    resolve();
                };
                img.onerror = () => {
                    // Desenhar placeholder circular se foto não carregar
                    const canvas = document.createElement('canvas');
                    canvas.width = fotoSize;
                    canvas.height = fotoSize;
                    const ctx = canvas.getContext('2d');
                    ctx.fillStyle = '#E5E7EB';
                    ctx.beginPath();
                    ctx.arc(fotoSize / 2, fotoSize / 2, fotoSize / 2, 0, 2 * Math.PI);
                    ctx.fill();
                    const placeholder = canvas.toDataURL('image/png');
                    doc.addImage(placeholder, 'PNG', fotoX, fotoY, fotoSize, fotoSize);
                    resolve();
                };
                img.src = fotoUrl;
            });
        } catch (err) {
            console.warn('Erro ao carregar foto do paciente:', err);
        }

        // Logo do Higeia no topo direito (reduzido)
        try {
            const logoPath = window.location.origin + '/images/higeia-icon-only.png';
            const logoImg = new Image();
            logoImg.crossOrigin = 'anonymous';
            
            await new Promise((resolve) => {
                logoImg.onload = () => {
                    try {
                        const canvas = document.createElement('canvas');
                        canvas.width = logoImg.width;
                        canvas.height = logoImg.height;
                        const ctx = canvas.getContext('2d');
                        ctx.drawImage(logoImg, 0, 0);
                        const imgData = canvas.toDataURL('image/png');
                        // Logo reduzido: de 30x30 para 20x20
                        doc.addImage(imgData, 'PNG', pageWidth - margin - 20, 10, 20, 20);
                    } catch (err) {
                        console.warn('Erro ao adicionar logo:', err);
                    }
                    resolve();
                };
                logoImg.onerror = () => resolve();
                logoImg.src = logoPath;
            });
        } catch (err) {
            console.warn('Erro ao processar logo:', err);
        }
        
        yPos += fotoSize + 10; // Espaço após foto

        // ============================================
        // DADOS DO PACIENTE
        // ============================================
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(14);
        doc.setFont(undefined, 'bold');
        doc.text('DADOS DO PACIENTE', margin, yPos);
        yPos += 10;

        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        doc.setFont(undefined, 'bold');
        doc.text('Nome Completo:', margin, yPos);
        doc.setFont(undefined, 'normal');
        const maxWidthNome = pageWidth - (margin * 2) - 45;
        yPos = adicionarTextoComQuebra(paciente.nome_completo || 'Não informado', margin + 45, yPos, maxWidthNome, 10, 6);
        yPos += 2;
        
        doc.setFont(undefined, 'bold');
        doc.text('Data de Nascimento:', margin, yPos);
        doc.setFont(undefined, 'normal');
        doc.text(`${paciente.data_nascimento_formatada || 'N/A'} (${paciente.idade || 'N/A'} anos)`, margin + 50, yPos);
        yPos += 7;
        
        if (paciente.condicao_medica) {
            doc.setFont(undefined, 'bold');
            doc.text('Condição Médica:', margin, yPos);
            doc.setFont(undefined, 'normal');
            const maxWidthCond = pageWidth - (margin * 2) - 50;
            yPos = adicionarTextoComQuebra(paciente.condicao_medica, margin + 50, yPos, maxWidthCond, 10, 6);
            yPos += 2;
        }
        
        if (paciente.endereco) {
            doc.setFont(undefined, 'bold');
            doc.text('Endereço:', margin, yPos);
            doc.setFont(undefined, 'normal');
            const maxWidthEnd = pageWidth - (margin * 2) - 35;
            yPos = adicionarTextoComQuebra(paciente.endereco, margin + 35, yPos, maxWidthEnd, 10, 6);
            yPos += 2;
        }
        
        doc.setFont(undefined, 'bold');
        doc.text('Status:', margin, yPos);
        doc.setFont(undefined, 'normal');
        doc.text(paciente.status || 'N/A', margin + 25, yPos);
        yPos += 12;

        // ============================================
        // ÚLTIMOS SINAIS VITAIS
        // ============================================
        if (ultimosSinaisVitais) {
            // Verificar se precisa de nova página
            if (yPos > pageHeight - 80) {
                doc.addPage();
                yPos = margin;
            }
            
            doc.setFontSize(14);
            doc.setFont(undefined, 'bold');
            doc.text('ÚLTIMOS SINAIS VITAIS', margin, yPos);
            yPos += 10;

            doc.setFontSize(10);
            doc.setFont(undefined, 'normal');
            if (ultimosSinaisVitais.temperatura) {
                doc.setFont(undefined, 'bold');
                doc.text('Temperatura:', margin, yPos);
                doc.setFont(undefined, 'normal');
                doc.text(`${ultimosSinaisVitais.temperatura}°C`, margin + 40, yPos);
                yPos += 7;
            }
            if (ultimosSinaisVitais.pressao_sistolica && ultimosSinaisVitais.pressao_diastolica) {
                doc.setFont(undefined, 'bold');
                doc.text('Pressão Arterial:', margin, yPos);
                doc.setFont(undefined, 'normal');
                doc.text(`${ultimosSinaisVitais.pressao_sistolica}/${ultimosSinaisVitais.pressao_diastolica} mmHg`, margin + 50, yPos);
                yPos += 7;
            }
            if (ultimosSinaisVitais.saturacao_o2) {
                doc.setFont(undefined, 'bold');
                doc.text('Saturação de O2:', margin, yPos);
                doc.setFont(undefined, 'normal');
                doc.text(`${ultimosSinaisVitais.saturacao_o2}%`, margin + 50, yPos);
                yPos += 7;
            }
            if (ultimosSinaisVitais.freq_cardiaca) {
                doc.setFont(undefined, 'bold');
                doc.text('Frequência Cardíaca:', margin, yPos);
                doc.setFont(undefined, 'normal');
                doc.text(`${ultimosSinaisVitais.freq_cardiaca} bpm`, margin + 60, yPos);
                yPos += 7;
            }
            yPos += 8;
        }

        // ============================================
        // PLANOS DE MEDICAÇÃO ATIVOS
        // ============================================
        if (planos.length > 0) {
            if (yPos > pageHeight - 100) {
                doc.addPage();
                yPos = margin;
            }

            doc.setFontSize(14);
            doc.setFont(undefined, 'bold');
            doc.text('MEDICAÇÕES ATIVAS', margin, yPos);
            yPos += 10;

            doc.setFontSize(10);
            doc.setFont(undefined, 'normal');
            planos.slice(0, 10).forEach((plano) => {
                if (yPos > pageHeight - 60) {
                    doc.addPage();
                    yPos = margin;
                }

                const dose = `${plano.dose || 'N/A'} ${plano.unidade_medida || ''}`;
                const frequencia = plano.frequencia_tipo === 'horas' 
                    ? `A cada ${plano.frequencia_intervalo_horas}h`
                    : `${plano.frequencia_vezes_ao_dia}x ao dia`;
                
                doc.setFont(undefined, 'bold');
                doc.text(`• ${plano.nome_medicamento || 'N/A'}`, margin + 5, yPos);
                yPos += 7;
                doc.setFontSize(9);
                doc.setFont(undefined, 'normal');
                doc.setTextColor(100, 100, 100);
                doc.text(`  Dose: ${dose} | ${frequencia}`, margin + 7, yPos);
                if (plano.instrucoes_especificas) {
                    yPos += 2;
                    const maxWidthInst = pageWidth - (margin * 2) - 7;
                    yPos = adicionarLabelEConteudo('  Instruções: ', plano.instrucoes_especificas, margin + 7, yPos, maxWidthInst, 9, 5);
                }
                yPos += 9;
                doc.setFontSize(10);
                doc.setTextColor(0, 0, 0);
            });
        }

        // ============================================
        // ALERTAS DE MEDICAÇÃO
        // ============================================
        if (alertasMedicacao.length > 0) {
            if (yPos > pageHeight - 80) {
                doc.addPage();
                yPos = margin;
            }

            doc.setFontSize(14);
            doc.setFont(undefined, 'bold');
            doc.setTextColor(220, 38, 38); // red-600
            doc.text('⚠ ALERTAS DE MEDICAÇÃO', margin, yPos);
            yPos += 10;

            doc.setFontSize(10);
            doc.setFont(undefined, 'normal');
            doc.setTextColor(0, 0, 0);
            alertasMedicacao.forEach((alerta) => {
                if (yPos > pageHeight - 60) {
                    doc.addPage();
                    yPos = margin;
                }
                doc.text(`• ${alerta.nome_medicamento}: Lote ${alerta.numero_lote} vence em ${alerta.data_validade}`, margin + 5, yPos);
                yPos += 8;
            });
            yPos += 8;
        }

        // ============================================
        // HISTÓRICO MÉDICO (ORDEM TEMPORAL)
        // ============================================
        if (todosRegistros.length > 0) {
            if (yPos > pageHeight - 100) {
                doc.addPage();
                yPos = margin;
            }

            doc.setFontSize(14);
            doc.setFont(undefined, 'bold');
            doc.setTextColor(0, 0, 0);
            doc.text('HISTÓRICO MÉDICO', margin, yPos);
            yPos += 10;

            doc.setFontSize(10);
            doc.setFont(undefined, 'normal');
            
            todosRegistros.forEach((registro) => {
                if (yPos > pageHeight - 80) {
                    doc.addPage();
                    yPos = margin;
                }
                
                // Formatar data de referência
                const dataFormatada = registro.dataReferencia 
                    ? new Date(registro.dataReferencia).toLocaleDateString('pt-BR')
                    : 'Data não informada';
                
                // Determinar tipo e ícone
                let tipoLabel = '';
                let descricao = '';
                
                switch (registro.tipoRegistro) {
                    case 'doenca':
                        tipoLabel = 'DOENÇA PRÉVIA';
                        descricao = registro.descricao;
                        break;
                    case 'cirurgia':
                        tipoLabel = 'CIRURGIA';
                        descricao = registro.descricao;
                        break;
                    case 'hospitalizacao':
                        tipoLabel = 'HOSPITALIZAÇÃO';
                        descricao = registro.descricao;
                        break;
                    case 'alergia':
                        tipoLabel = 'ALERGIA';
                        descricao = registro.descricao;
                        break;
                    case 'vacina':
                        tipoLabel = 'VACINA';
                        descricao = registro.nome_vacina || registro.descricao || 'Vacina';
                        break;
                    case 'exame':
                        tipoLabel = 'EXAME';
                        descricao = registro.nome_exame || registro.descricao || 'Exame';
                        break;
                }
                
                // Título com tipo e data
                doc.setFont(undefined, 'bold');
                doc.setTextColor(0, 0, 0);
                doc.text(`[${tipoLabel}] ${dataFormatada}`, margin, yPos);
                yPos += 7;
                
                // Descrição
                doc.setFont(undefined, 'normal');
                doc.text(`• ${descricao}`, margin + 5, yPos);
                yPos += 7;
                
                // Detalhes específicos por tipo
                doc.setFontSize(9);
                doc.setTextColor(100, 100, 100);
                
                if (registro.tipoRegistro === 'doenca') {
                    if (registro.observacoes) {
                        const maxWidthObs = pageWidth - (margin * 2) - 7;
                        yPos = adicionarLabelEConteudo('  Observações: ', registro.observacoes, margin + 7, yPos, maxWidthObs, 9, 5);
                        yPos += 2;
                    }
                } else if (registro.tipoRegistro === 'cirurgia') {
                    const maxWidthDet = pageWidth - (margin * 2) - 7;
                    if (registro.local_cirurgia) {
                        yPos = adicionarLabelEConteudo('  Local: ', registro.local_cirurgia, margin + 7, yPos, maxWidthDet, 9, 5);
                        yPos += 2;
                    }
                    if (registro.medico_responsavel) {
                        yPos = adicionarLabelEConteudo('  Médico Responsável: ', registro.medico_responsavel, margin + 7, yPos, maxWidthDet, 9, 5);
                        yPos += 2;
                    }
                    if (registro.observacoes) {
                        yPos = adicionarLabelEConteudo('  Observações: ', registro.observacoes, margin + 7, yPos, maxWidthDet, 9, 5);
                        yPos += 2;
                    }
                } else if (registro.tipoRegistro === 'hospitalizacao') {
                    const maxWidthDet = pageWidth - (margin * 2) - 7;
                    if (registro.data_internacao) {
                        const dataInternacao = new Date(registro.data_internacao).toLocaleDateString('pt-BR');
                        doc.text(`  Data de Internação: ${dataInternacao}`, margin + 7, yPos);
                        yPos += 6;
                    }
                    if (registro.data_alta) {
                        const dataAlta = new Date(registro.data_alta).toLocaleDateString('pt-BR');
                        doc.text(`  Data de Alta: ${dataAlta}`, margin + 7, yPos);
                        yPos += 6;
                    }
                    if (registro.motivo_internacao) {
                        yPos = adicionarLabelEConteudo('  Motivo: ', registro.motivo_internacao, margin + 7, yPos, maxWidthDet, 9, 5);
                        yPos += 2;
                    }
                    if (registro.diagnostico_alta) {
                        yPos = adicionarLabelEConteudo('  Diagnóstico de Alta: ', registro.diagnostico_alta, margin + 7, yPos, maxWidthDet, 9, 5);
                        yPos += 2;
                    }
                    if (registro.observacoes) {
                        yPos = adicionarLabelEConteudo('  Observações: ', registro.observacoes, margin + 7, yPos, maxWidthDet, 9, 5);
                        yPos += 2;
                    }
                } else if (registro.tipoRegistro === 'alergia') {
                    const maxWidthDet = pageWidth - (margin * 2) - 7;
                    if (registro.tipo) {
                        const tipoAlergia = registro.tipo === 'medicamento' ? 'Medicamento' : 
                                          registro.tipo === 'alimento' ? 'Alimento' : 'Outro';
                        doc.text(`  Tipo: ${tipoAlergia}`, margin + 7, yPos);
                        yPos += 6;
                    }
                    if (registro.severidade) {
                        doc.text(`  Severidade: ${registro.severidade}`, margin + 7, yPos);
                        yPos += 6;
                    }
                    if (registro.reacao) {
                        yPos = adicionarLabelEConteudo('  Reação: ', registro.reacao, margin + 7, yPos, maxWidthDet, 9, 5);
                        yPos += 2;
                    }
                    if (registro.tratamento) {
                        yPos = adicionarLabelEConteudo('  Tratamento: ', registro.tratamento, margin + 7, yPos, maxWidthDet, 9, 5);
                        yPos += 2;
                    }
                    if (registro.observacoes) {
                        yPos = adicionarLabelEConteudo('  Observações: ', registro.observacoes, margin + 7, yPos, maxWidthDet, 9, 5);
                        yPos += 2;
                    }
                } else if (registro.tipoRegistro === 'vacina') {
                    if (registro.fabricante) {
                        doc.text(`  Fabricante: ${registro.fabricante}`, margin + 7, yPos);
                        yPos += 6;
                    }
                    if (registro.lote) {
                        doc.text(`  Lote: ${registro.lote}`, margin + 7, yPos);
                        yPos += 6;
                    }
                    if (registro.dose_numero) {
                        doc.text(`  Dose: ${registro.dose_numero}ª dose`, margin + 7, yPos);
                        yPos += 6;
                    }
                    if (registro.local_aplicacao) {
                        const maxWidthDet = pageWidth - (margin * 2) - 7;
                        yPos = adicionarTextoComQuebra(`  Local de Aplicação: ${registro.local_aplicacao}`, margin + 7, yPos, maxWidthDet, 9, 5);
                        yPos += 2;
                    }
                    if (registro.profissional_aplicador) {
                        const maxWidthDet = pageWidth - (margin * 2) - 7;
                        yPos = adicionarTextoComQuebra(`  Profissional: ${registro.profissional_aplicador}`, margin + 7, yPos, maxWidthDet, 9, 5);
                        yPos += 2;
                    }
                    if (registro.via_administracao) {
                        doc.text(`  Via: ${registro.via_administracao}`, margin + 7, yPos);
                        yPos += 6;
                    }
                    if (registro.proxima_dose) {
                        const dataProxima = new Date(registro.proxima_dose).toLocaleDateString('pt-BR');
                        doc.text(`  Próxima Dose: ${dataProxima}`, margin + 7, yPos);
                        yPos += 6;
                    }
                    if (registro.reacao_adversa) {
                        const maxWidthDet = pageWidth - (margin * 2) - 7;
                        yPos = adicionarLabelEConteudo('  Reação Adversa: ', registro.reacao_adversa, margin + 7, yPos, maxWidthDet, 9, 5);
                        yPos += 2;
                    }
                    if (registro.observacoes) {
                        const maxWidthDet = pageWidth - (margin * 2) - 7;
                        yPos = adicionarLabelEConteudo('  Observações: ', registro.observacoes, margin + 7, yPos, maxWidthDet, 9, 5);
                        yPos += 2;
                    }
                } else if (registro.tipoRegistro === 'exame') {
                    const maxWidthDet = pageWidth - (margin * 2) - 7;
                    if (registro.tipo_exame) {
                        doc.text(`  Tipo: ${registro.tipo_exame}`, margin + 7, yPos);
                        yPos += 6;
                    }
                    if (registro.data_resultado) {
                        const dataResultado = new Date(registro.data_resultado).toLocaleDateString('pt-BR');
                        doc.text(`  Data do Resultado: ${dataResultado}`, margin + 7, yPos);
                        yPos += 6;
                    }
                    if (registro.local_realizacao) {
                        yPos = adicionarLabelEConteudo('  Local: ', registro.local_realizacao, margin + 7, yPos, maxWidthDet, 9, 5);
                        yPos += 2;
                    }
                    if (registro.medico_solicitante) {
                        yPos = adicionarLabelEConteudo('  Médico Solicitante: ', registro.medico_solicitante, margin + 7, yPos, maxWidthDet, 9, 5);
                        yPos += 2;
                    }
                    if (registro.resultado) {
                        yPos = adicionarLabelEConteudo('  Resultado: ', registro.resultado, margin + 7, yPos, maxWidthDet, 9, 5);
                        yPos += 2;
                    }
                    if (registro.interpretacao) {
                        yPos = adicionarLabelEConteudo('  Interpretação: ', registro.interpretacao, margin + 7, yPos, maxWidthDet, 9, 5);
                        yPos += 2;
                    }
                    if (registro.observacoes) {
                        yPos = adicionarLabelEConteudo('  Observações: ', registro.observacoes, margin + 7, yPos, maxWidthDet, 9, 5);
                        yPos += 2;
                    }
                }
                
                yPos += 5;
                doc.setFontSize(10);
                doc.setTextColor(0, 0, 0);
            });
            yPos += 5;
        }

        // ============================================
        // HISTÓRICO DE SINAIS VITAIS (ÚLTIMOS 30 DIAS)
        // ============================================
        if (sinaisVitais.length > 0) {
            if (yPos > pageHeight - 100) {
                doc.addPage();
                yPos = margin;
            }

            doc.setFontSize(14);
            doc.setFont(undefined, 'bold');
            doc.text('HISTÓRICO DE SINAIS VITAIS (Últimos 30 dias)', margin, yPos);
            yPos += 10;

            // Cabeçalho da tabela
            doc.setFontSize(9);
            doc.setDrawColor(...corEmerald);
            doc.setFillColor(...corEmerald);
            doc.rect(margin, yPos, pageWidth - (margin * 2), 7, 'F');
            doc.setTextColor(255, 255, 255);
            doc.setFont(undefined, 'bold');
            doc.text('Data/Hora', margin + 2, yPos + 5);
            doc.text('Temp', margin + 46, yPos + 5);
            doc.text('PA', margin + 71, yPos + 5);
            doc.text('Sat O2', margin + 96, yPos + 5);
            doc.text('FC', margin + 121, yPos + 5);
            yPos += 7;
            doc.setTextColor(0, 0, 0);
            doc.setFont(undefined, 'normal');

            // Dados
            sinaisVitais.slice(0, 15).forEach((sv) => {
                if (yPos > pageHeight - 50) {
                    doc.addPage();
                    yPos = margin;
                    // Redesenhar cabeçalho da tabela
                    doc.setFillColor(...corEmerald);
                    doc.rect(margin, yPos, pageWidth - (margin * 2), 7, 'F');
                    doc.setTextColor(255, 255, 255);
                    doc.setFont(undefined, 'bold');
                    doc.text('Data/Hora', margin + 2, yPos + 5);
                    doc.text('Temp', margin + 46, yPos + 5);
                    doc.text('PA', margin + 71, yPos + 5);
                    doc.text('Sat O2', margin + 96, yPos + 5);
                    doc.text('FC', margin + 121, yPos + 5);
                    yPos += 7;
                    doc.setTextColor(0, 0, 0);
                    doc.setFont(undefined, 'normal');
                }

                const dataHora = new Date(sv.data_registro).toLocaleString('pt-BR', {
                    day: '2-digit',
                    month: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit'
                });
                const isEven = sinaisVitais.indexOf(sv) % 2 === 0;
                if (isEven) {
                    doc.setFillColor(245, 245, 245);
                    doc.rect(margin, yPos, pageWidth - (margin * 2), 6, 'F');
                }
                doc.setDrawColor(200, 200, 200);
                doc.rect(margin, yPos, pageWidth - (margin * 2), 6);

                doc.setFontSize(8);
                doc.text(dataHora, margin + 2, yPos + 4);
                doc.text(sv.temperatura ? `${sv.temperatura}°C` : '--', margin + 46, yPos + 4);
                doc.text(
                    sv.pressao_sistolica && sv.pressao_diastolica 
                        ? `${sv.pressao_sistolica}/${sv.pressao_diastolica}` 
                        : '--',
                    margin + 71, yPos + 4
                );
                doc.text(sv.saturacao_o2 ? `${sv.saturacao_o2}%` : '--', margin + 96, yPos + 4);
                doc.text(sv.freq_cardiaca ? `${sv.freq_cardiaca}` : '--', margin + 121, yPos + 4);
                yPos += 6;
            });
            yPos += 5;
        }

        // ============================================
        // HISTÓRICO DE CUIDADOS RECENTES
        // ============================================
        if (cuidados.length > 0) {
            if (yPos > pageHeight - 100) {
                doc.addPage();
                yPos = margin;
            }

            doc.setFontSize(14);
            doc.setFont(undefined, 'bold');
            doc.text('HISTÓRICO DE CUIDADOS RECENTES', margin, yPos);
            yPos += 10;

            doc.setFontSize(10);
            doc.setFont(undefined, 'normal');
            cuidados.slice(0, 10).forEach((cuidado) => {
                if (yPos > pageHeight - 60) {
                    doc.addPage();
                    yPos = margin;
                }

                const dataHora = new Date(cuidado.data_hora_registro).toLocaleString('pt-BR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                });

                doc.setFont(undefined, 'bold');
                doc.text(`[${dataHora}] ${cuidado.tipo_registro || 'Registro'}`, margin + 5, yPos);
                yPos += 7;
                doc.setFontSize(9);
                doc.setFont(undefined, 'normal');
                doc.setTextColor(100, 100, 100);
                if (cuidado.observacoes) {
                    const maxWidthObs = pageWidth - (margin * 2) - 7;
                    yPos = adicionarTextoComQuebra(`  ${cuidado.observacoes}`, margin + 7, yPos, maxWidthObs, 9, 5);
                    yPos += 2;
                }
                if (cuidado.usuario_registro) {
                    doc.text(`  Profissional: ${cuidado.usuario_registro}`, margin + 7, yPos);
                    yPos += 6;
                }
                yPos += 6;
                doc.setFontSize(10);
                doc.setTextColor(0, 0, 0);
            });
            yPos += 5;
        }

        // Última página - Informações adicionais
        if (yPos > pageHeight - 150) {
            doc.addPage();
            yPos = margin;
        } else {
            yPos += 10;
        }

        doc.setFontSize(14);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text('INFORMAÇÕES ADICIONAIS', margin, yPos);
        yPos += 12;

        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        doc.text('Este documento contém informações médicas confidenciais.', margin, yPos);
        yPos += 8;
        doc.text('Destina-se exclusivamente para uso em procedimentos médicos e internação hospitalar.', margin, yPos);
        yPos += 12;

        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text('OBSERVAÇÕES IMPORTANTES:', margin, yPos);
        yPos += 10;

        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        doc.text('• Verificar alergias conhecidas antes de administrar medicações', margin + 5, yPos);
        yPos += 7;
        doc.text('• Confirmar equipamentos em uso antes de procedimentos', margin + 5, yPos);
        yPos += 7;
        doc.text('• Manter continuidade dos planos de medicação ativos', margin + 5, yPos);
        yPos += 7;
        doc.text('• Monitorar sinais vitais conforme protocolo hospitalar', margin + 5, yPos);
        yPos += 12;

        doc.setFontSize(10);
        doc.text(`Documento gerado automaticamente pelo sistema Higeia.tech`, margin, yPos);
        yPos += 7;
        doc.text(`Data de geração: ${dataAtual} às ${horaAtual}`, margin, yPos);

        // ============================================
        // RODAPÉ EM TODAS AS PÁGINAS
        // ============================================
        const totalPages = doc.internal.pages.length - 1;
        for (let i = 1; i <= totalPages; i++) {
            doc.setPage(i);
            
            // Rodapé (barra verde apenas na primeira página)
            doc.setFontSize(8);
            doc.setTextColor(128, 128, 128);
            doc.setFont(undefined, 'normal');
            doc.text(
                `Página ${i} de ${totalPages} | Gerado em ${dataAtual} às ${horaAtual} pela Higeia IA`,
                pageWidth / 2,
                pageHeight - 10,
                { align: 'center' }
            );
        }

        // Download
        const nomeArquivo = `Prontuario_${paciente.nome_completo.replace(/\s+/g, '_')}_${dataAtual.replace(/\//g, '-')}.pdf`;
        doc.save(nomeArquivo);

    } catch (error) {
        console.error('Erro ao gerar PDF do prontuário:', error);
        throw new Error('Erro ao gerar o PDF do prontuário. Por favor, tente novamente.');
    }
};

